import { useEffect, useRef, useState } from 'react'
import type { UploadBridge } from '@/features/editor/lib/uploadBridge'

interface Zone {
  onDrop: (files: File[], fromCanvas?: boolean) => void
  open: () => void
  /** Снять файл по просьбе холста. */
  onRemove?: () => void
  progress: number | null
  disabled: boolean
  /** Откуда загрузка, если это знает её владелец (видео: хранилище над панелью).
   *  Тогда признак переживает уход из блока — локальное состояние умерло бы с ним. */
  fromCanvas?: boolean
  /** Прогресс на холсте всегда, откуда бы ни начали (видео: полоса и там, и в панели). */
  alwaysOnCanvas?: boolean
}

/**
 * Зона загрузки в панели обслуживает холст: принимает его файлы и отдаёт прогресс.
 * Полоса загрузки — только там, ОТКУДА бросили файл: загрузка с холста идёт
 * полосой на холсте, панель её не дублирует (`fromCanvas`), и наоборот. Исключение —
 * `alwaysOnCanvas`: видео показывает загрузку в обоих местах.
 */
export const useCanvasBridge = (bridge: UploadBridge | undefined, zone: Zone) => {
  const zoneRef = useRef(zone)
  zoneRef.current = zone
  const [fromCanvas, setFromCanvas] = useState(false)
  useEffect(
    () =>
      bridge?.onRequest((file) => {
        const z = zoneRef.current
        if (z.disabled || z.progress !== null) return
        setFromCanvas(true)
        if (file) z.onDrop([file], true)
        else z.open()
      }),
    [bridge],
  )
  useEffect(
    () =>
      bridge?.onRemoveRequest(() => {
        const z = zoneRef.current
        if (!z.disabled && z.progress === null) z.onRemove?.()
      }),
    [bridge],
  )
  // Загрузка кончилась — следующая снова «своя», пока холст не попросит.
  useEffect(() => {
    if (zone.progress === null) setFromCanvas(false)
  }, [zone.progress])
  const shownOnCanvas = zone.fromCanvas ?? fromCanvas
  useEffect(
    () => bridge?.setProgress(shownOnCanvas || zone.alwaysOnCanvas ? zone.progress : null),
    [bridge, shownOnCanvas, zone.alwaysOnCanvas, zone.progress],
  )
  useEffect(() => () => bridge?.setProgress(null), [bridge])
  return { fromCanvas: shownOnCanvas }
}
